import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { loginPhoneCode } from '@/features/users/loginPhoneError';

/**
 * Staff management (slice 17w).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts`
 * (`StaffMemberZ`, seam S1) and are never re-declared from the screen's point
 * of view.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THERE IS NO `passwordHash` ON THIS SHAPE, AND THAT IS THE POINT.
 * ─────────────────────────────────────────────────────────────────────────────
 * The list carries `hasPassword` — a boolean the server derives in SQL — and
 * NEVER the hash. The plaintext a super-admin types on the form is sent once,
 * to `POST /api/admin/staff`, and is cleared from component state the moment the
 * request resolves. It is never stored here and never rendered.
 *
 * The ROLE is never a form field either. `createStaff` on the server hardcodes
 * `admin_staff` and has no parameter to override — the API cannot mint a
 * super_admin, so the UI must not imply it can. Role is shown as read-only text.
 */

export interface StaffMember {
  id: string;
  name: string;
  phone: string;
  role: 'super_admin' | 'admin_staff';
  /** ISO timestamp when disabled, or null when enabled. */
  disabledAt: string | null;
  createdAt: string;
  /** Whether a login password is set — never the hash itself. */
  hasPassword: boolean;
}

export interface StaffList {
  staff: StaffMember[];
}

export const staffQueryKey = ['admin', 'staff'] as const;

export function useStaff(): UseQueryResult<StaffList> {
  return useQuery({
    queryKey: staffQueryKey,
    queryFn: ({ signal }) => apiJson<StaffList>('/api/admin/staff', { signal }),
    // A 4xx is a decision (a non-super-admin sees 403), not a hiccup — retrying
    // it just delays the answer, exactly as `useTenants` reasons.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

export interface CreateStaffBody {
  name: string;
  phone: string;
  /** Sent once. Never stored in this module; the caller clears it after submit. */
  password: string;
}

/**
 * The reasons a create can fail that the UI translates into a sentence a person
 * can act on. Anything else surfaces as a generic failure.
 */
export type CreateStaffFailure =
  | 'duplicate_phone'
  | 'invalid'
  | 'phone_is_sim'
  | 'phone_in_use'
  | 'unknown';

export function classifyCreateError(error: unknown): CreateStaffFailure {
  // SLICE 52 — "one phone, one identity across staff and tenants" (Mahad). Staff
  // create now runs the shared login-phone check, so it can reject a phone that
  // belongs to a tracker SIM or to a tenant user, each with its own server code.
  // These are read from the BODY, not the status, because a 409 here can equally
  // be a duplicate STAFF phone (`phone_is_staff` / the old `duplicate_phone`).
  const code = loginPhoneCode(error);
  if (code === 'phone_is_sim') return 'phone_is_sim';
  if (code === 'phone_in_use') return 'phone_in_use';
  // A clash with another staff member, and an unparseable number, keep the
  // messages this form has always used.
  if (code === 'phone_is_staff') return 'duplicate_phone';
  if (code === 'phone_invalid') return 'invalid';
  if (error instanceof ApiError) {
    // 409 `{error:'duplicate_phone'}` — the phone already belongs to a staff
    // member. 400 `{error:'invalid_body'}` — a phone that will not parse to
    // E.164 (the password floor is enforced on the form before we get here).
    if (error.status === 409) return 'duplicate_phone';
    if (error.status === 400) return 'invalid';
  }
  return 'unknown';
}

export function useCreateStaff(): UseMutationResult<StaffMember, Error, CreateStaffBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStaffBody) =>
      apiJson<StaffMember>('/api/admin/staff', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffQueryKey });
    },
  });
}

/** Why a disable can be refused that the UI names specifically. */
export type SetDisabledFailure = 'last_super_admin' | 'unknown';

export function classifySetDisabledError(error: unknown): SetDisabledFailure {
  // 409 `{error:'last_super_admin'}` — disabling the only enabled super-admin
  // would lock everyone out of staff management, so the server refuses it.
  if (error instanceof ApiError && error.status === 409) return 'last_super_admin';
  return 'unknown';
}

export function useSetStaffDisabled(): UseMutationResult<
  StaffMember,
  Error,
  { id: string; disabled: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disabled }) =>
      apiJson<StaffMember>(`/api/admin/staff/${id}/${disabled ? 'disable' : 'enable'}`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffQueryKey });
    },
  });
}
